import { useTranslation } from 'react-i18next';
import PrimaryButton from '../../../components/buttons/PrimaryButton';
import { Plus } from 'react-bootstrap-icons';
import {
  Select,
  Modal,
  Descriptions,
  Tag,
  Spin,
  DatePicker,
  InputNumber,
  Button,
  Row,
  Col,
  Popover,
  Checkbox,
  Image,
} from 'antd';
import DataTable from '../../../components/common/DataTable';
import { Link } from 'react-router-dom';
import { useLocalData } from '../../../hooks/useLocalData';
import { useEffect, useState, useCallback } from 'react';
import fetcher from '../../../helpers/fetcher';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { ENVIRONMENT_CONFIG } from '../../../constants/environmentConfig';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import dayjs from 'dayjs';
import PrimarySquareBtn from '../../../components/buttons/PrimarySquareBtn';
import { getPriceList } from './getData';
import { PriceStatusEnum } from '../../../enums/PriceStatusEnum';
import { UserTypes } from '../../../enums/UserTypeEnum';
import { normalize } from '../../../utils/normalizeWords';
import { disableFutureDates } from '../../../utils/disableDates';
import { downloadAllPriceRecords } from '../getData';
import { downloadCSVFromUrl } from '../../../helpers/utilityHelper';
import { useIsOnlineContext } from '../../../contexts/IsOnlineContext';

interface MyPricesProps {
  triggeredFrom: string;
}

type PriceFilters = {
  productCategories?: number[] | string[];
  baskets?: number[];
  products?: string[];
  dateRange?: [dayjs.Dayjs, dayjs.Dayjs] | null;
  priceFilterValue?: number | null;
  priceFilterCondition?: string | null;
  priceFilterValue2?: number | null;
  notInSuggestedPresentation?: boolean;
  notInSuggestedBrand?: boolean;
};

const MyPrices: React.FC<MyPricesProps> = ({ triggeredFrom }) => {
  const { t } = useTranslation(['inspector', 'price']);

  const { data: prices } = useLocalData({
    entity: 'priceRecord',
    autoSync: true,
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const [priceData, setPriceData] = useState<any>(prices);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isLoadingPrices, setIsLoadingPrices] = useState<boolean>(false);
  const [pageSize] = useState<number>(8);
  const [totalPrices, setTotalPrices] = useState<number>(0);
  const [isViewModalVisible, setIsViewModalVisible] = useState<boolean>(false);
  const [selectedPrice, setSelectedPrice] = useState<any>(null);
  const [isLoadingPriceDetails, setIsLoadingPriceDetails] = useState<boolean>(false);
  const { errorNotification } = useNotificationContext();

  const [selectedProductCategories, setSelectedProductCategories] = useState<string[]>([]);
  const [selectedBaskets, setSelectedBaskets] = useState<number[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [priceFilterValue, setPriceFilterValue] = useState<number | null>(null);
  const [priceFilterCondition, setPriceFilterCondition] = useState<
    'equal' | 'less' | 'greater' | 'between' | undefined
  >(undefined);
  const [priceFilterValue2, setPriceFilterValue2] = useState<number | null>(null);

  const [productCategories, setProductCategories] = useState<any[]>([]);
  const [baskets, setBaskets] = useState<any[]>([]);
  const [presentations, setPresentations] = useState<any[]>([]);
  const [isLoadingFilterData, setIsLoadingFilterData] = useState<boolean>(false);
  const [categoryDetails, setCategoryDetails] = useState<Record<number, any>>({});
  const [enableClearFiltersBtn, setEnableClearFiltersBtn] = useState<boolean>(false);
  const [filteredPresentations, setFilteredPresentations] = useState<any[]>([]);

  const [isNotInSuggestedPresentation, setIsNotInSuggestedPresentation] = useState<boolean>(false);
  const [isNotInSuggestedBrand, setIsNotInSuggestedBrand] = useState<boolean>(false);
  const [isdownloadingPrices, setIsdownloadingPrices] = useState<boolean>(false);
  const { isOnline } = useIsOnlineContext();

  const enrichPriceWithProductInspection = useCallback(async (price: any) => {
    try {
      let productData = null;
      if (price?.product_ref_id) {
        productData = await referenceDataSyncService.getLocalProduct(
          price.product_ref_id as string
        );
      }

      let inspectionData = null;
      if (price?.inspectionRefId) {
        inspectionData = await referenceDataSyncService.getInspectionsByRefId(
          price.inspectionRefId as string
        );
      }

      let locationData = null;
      if (price.location_id) {
        locationData = await referenceDataSyncService.getLocationById(price.location_id);
      }

      const enrichedData = {
        ...price,
        product: productData,
        inspection: {
          ...inspectionData,
          location: locationData,
        },
      };

      return enrichedData;
    } catch (error) {
      console.error('Error enriching price data:', error);
      return price;
    }
  }, []);

  const getProductInspectionDataById = useCallback(
    async (prices: any) => {
      try {
        const priceWithProductData = await Promise.all(
          prices.map(enrichPriceWithProductInspection)
        );

        setPriceData(priceWithProductData);
      } catch (error) {
        console.error('Error enriching prices with product data:', error);
        setPriceData(prices);
      }
    },
    [enrichPriceWithProductInspection]
  );

  const buildFilterParams = (
    base: { page?: number; limit?: number } = {},
    filters?: PriceFilters
  ) => {
    const filterParams: any = { ...base };

    if (filters) {
      const {
        productCategories,
        baskets,
        products,
        dateRange,
        priceFilterValue,
        priceFilterCondition,
        priceFilterValue2,
        notInSuggestedPresentation,
        notInSuggestedBrand,
      } = filters;

      if (Array.isArray(productCategories) && productCategories.length > 0) {
        filterParams.productCategoryIds = productCategories;
      }

      if (Array.isArray(baskets) && baskets.length > 0) {
        filterParams.basketIds = baskets;
      }

      if (Array.isArray(products) && products.length > 0) {
        filterParams.productRefIds = products;
      }

      if (dateRange && dateRange[0]) {
        filterParams.dateFrom = dateRange[0].valueOf();
      }

      if (dateRange && dateRange[1]) {
        filterParams.dateTo = dateRange[1].endOf('day').valueOf();
      }

      if (
        priceFilterValue !== null &&
        priceFilterValue !== undefined &&
        !isNaN(Number(priceFilterValue))
      ) {
        filterParams.priceFilterValue = Number(priceFilterValue);
        filterParams.priceFilterCondition = priceFilterCondition;

        if (
          priceFilterValue2 !== null &&
          priceFilterValue2 !== undefined &&
          !isNaN(Number(priceFilterValue2))
        ) {
          filterParams.priceFilterValue2 = Number(priceFilterValue2);
        }
      }

      if (notInSuggestedPresentation === true) {
        filterParams.notInSuggestedPresentation = 'no';
      }

      if (notInSuggestedBrand === true) {
        filterParams.notInSuggestedBrand = 'no';
      }
    }

    if (triggeredFrom === 'citizenPortal') {
      filterParams.role = UserTypes.CITIZEN;
    }

    return filterParams;
  };

  const getAllPriceList = useCallback(
    async (page: number = currentPage, filters?: any) => {
      setIsLoadingPrices(true);
      try {
        const filterParams = buildFilterParams({ page, limit: pageSize }, filters);

        const response: any = await getPriceList(filterParams);

        if (response.statusText === 'SUCCESS') {
          const prices = response.data?.data || response.data || [];
          const total = response.data?.total || 0;
          setPriceData(prices);
          setTotalPrices(total);
          setCurrentPage(page);
          getProductInspectionDataById(prices);
        } else {
          console.error(t('priceFetchError'), response);
        }
      } catch (error) {
        console.error(t('priceFetchError'), error);
      } finally {
        setIsLoadingPrices(false);
      }
    },
    [pageSize, errorNotification, t, getProductInspectionDataById]
  );

  const handlePageChange = (page: number) => {
    const filters = {
      productCategories: selectedProductCategories,
      baskets: selectedBaskets,
      products: selectedProducts,
      dateRange,
      priceFilterValue,
      priceFilterValue2,
      priceFilterCondition,
    };
    getAllPriceList(page, filters);
  };

  const downloadCSVFn = async () => {
    try {
      setIsdownloadingPrices(true);
      let filters: PriceFilters = {
        productCategories: selectedProductCategories,
        baskets: selectedBaskets,
        products: selectedProducts,
        dateRange: dateRange,
        priceFilterValue,
        priceFilterCondition,
        priceFilterValue2,
      };

      if (isNotInSuggestedPresentation) {
        filters.notInSuggestedPresentation = true;
      }

      if (isNotInSuggestedBrand) {
        filters.notInSuggestedBrand = true;
      }

      const filterParams = buildFilterParams({}, filters);

      const response = await downloadAllPriceRecords(filterParams);

      if (response.statusText === 'SUCCESS') {
        const { url, fileName } = response.data;
        downloadCSVFromUrl(url, fileName);
      } else {
        console.error(t('inspectionDetailDownloadError'), response);
        errorNotification(t('inspectionDetailDownloadError'));
      }
    } catch (error) {
      console.error(t('inspectionDetailDownloadError'), error);
      errorNotification(t('inspectionDetailDownloadError'));
      setIsdownloadingPrices(false);
    } finally {
      setIsdownloadingPrices(false);
    }
  };

  const handleViewPrice = async (priceId: string | number) => {
    setIsLoadingPriceDetails(true);
    setIsViewModalVisible(true);

    try {
      const price = priceData.find((p: any) => p.id === priceId);
      if (price) {
        setSelectedPrice(price);
      } else {
        const response = await fetcher.get(
          `${API_ENDPOINTS.PRICE.PRICE}/${priceId}`,
          {},
          {},
          ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
        );

        if (response.statusText === 'SUCCESS' && response.data) {
          setSelectedPrice(response.data);
        } else {
          errorNotification(t('priceDetailFetchError'));
        }
      }
    } catch (error) {
      console.error(t('priceDetailFetchError'), error);
      errorNotification(t('priceDetailFetchError'));
    } finally {
      setIsLoadingPriceDetails(false);
    }
  };

  const handleCloseModal = () => {
    setIsViewModalVisible(false);
    setSelectedPrice(null);
  };

  const fetchFilterData = useCallback(async () => {
    setIsLoadingFilterData(true);
    try {
      const categoriesData = await referenceDataSyncService.getProductCategories();
      setProductCategories(categoriesData);

      const basketsData = await referenceDataSyncService.getBaskets();
      setBaskets(basketsData);

      const presentationsData = await referenceDataSyncService.getPresentationsForFilters();
      setPresentations(presentationsData);
      setFilteredPresentations(presentationsData);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    } finally {
      setIsLoadingFilterData(false);
    }
  }, [errorNotification]);

  const getCategoryDetails = useCallback(
    async (categoryId: number) => {
      if (categoryDetails[categoryId]) {
        return categoryDetails[categoryId];
      }

      try {
        const category = productCategories.find((cat) => cat.id === categoryId);
        if (category) {
          setCategoryDetails((prev) => ({ ...prev, [categoryId]: category }));
          return category;
        }
      } catch (error) {
        console.error('Error fetching category details:', error);
      }
      return null;
    },
    [categoryDetails, productCategories]
  );

  const clearAllFilters = () => {
    setSelectedProductCategories([]);
    setSelectedBaskets([]);
    setSelectedProducts([]);
    setDateRange(null);
    setPriceFilterValue(null);
    setPriceFilterValue2(null);
    setPriceFilterCondition(undefined);
    setIsNotInSuggestedPresentation(false);
    setIsNotInSuggestedBrand(false);

    getAllPriceList(1, {});
  };

  useEffect(() => {
    getAllPriceList(1);
    fetchFilterData();
  }, [fetchFilterData]);

  useEffect(() => {
    const filters = {
      productCategories: selectedProductCategories,
      baskets: selectedBaskets,
      products: selectedProducts,
      dateRange,
      priceFilterValue,
      priceFilterValue2,
      priceFilterCondition,
      notInSuggestedPresentation: isNotInSuggestedPresentation,
      notInSuggestedBrand: isNotInSuggestedBrand,
    };

    const hasNonPriceFilters =
      selectedProductCategories.length > 0 ||
      selectedBaskets.length > 0 ||
      selectedProducts.length > 0 ||
      dateRange !== null ||
      isNotInSuggestedPresentation === true ||
      isNotInSuggestedBrand === true;

    const hasPriceFilters =
      (priceFilterValue !== null && priceFilterValue !== undefined) ||
      (priceFilterValue2 !== null && priceFilterValue2 !== undefined);

    const shouldApplyFilters = hasNonPriceFilters || hasPriceFilters;

    if (shouldApplyFilters) {
      getAllPriceList(1, filters);
      setEnableClearFiltersBtn(true);
    } else {
      getAllPriceList(1, {});
      setEnableClearFiltersBtn(false);
    }
  }, [
    selectedProductCategories,
    selectedBaskets,
    selectedProducts,
    dateRange,
    priceFilterValue,
    priceFilterValue2,
    priceFilterCondition,
    isNotInSuggestedPresentation,
    isNotInSuggestedBrand,
  ]);

  const handleBasketChange = async (basketIds: number[]) => {
    setSelectedBaskets(basketIds);

    setSelectedProducts([]);

    if (basketIds.length === 0) {
      setFilteredPresentations(presentations);
    } else {
      // Get presentations for all selected baskets
      try {
        const filteredPresentations =
          await referenceDataSyncService.getPresentationsForFilters(basketIds);

        setFilteredPresentations(filteredPresentations);
      } catch (error) {
        console.error('Error fetching presentations by basket:', error);
        errorNotification('Failed to load presentations for selected baskets');
        setFilteredPresentations([]);
      }
    }
  };

  // Define table columns
  const columns = [
    {
      key: 'product',
      title: t('product'),
      dataIndex: 'product',
      width: 200,
      render: (value: any, record: any) => (
        <div>
          <div className="font-medium">{record.product?.name || 'N/A'}</div>
        </div>
      ),
    },
    {
      key: 'category',
      title: t('category'),
      dataIndex: 'category',
      width: 150,
      render: (value: any, record: any) => {
        const categoryId = record.product_category_id;
        if (!categoryId) {
          return <span className="text-gray-600">N/A</span>;
        }

        const category = productCategories.find((cat) => cat.id === categoryId);
        if (!category) {
          return <span className="text-gray-600">N/A</span>;
        }

        return (
          <span className="text-sm text-gray-700">
            {category && category.category
              ? `${category.mainCategory} > ${category.category}`
              : category && category.mainCategory
                ? `${category.mainCategory}`
                : 'N/A'}
          </span>
        );
      },
    },
    {
      key: 'baskets',
      title: t('baskets'),
      dataIndex: 'baskets',
      width: 80,
      align: 'center' as const,
      render: (value: any, record: any) => {
        const basketIds = record.basket_ids || [];
        const count = basketIds.length;
        if (count === 0) {
          return <span className="text-gray-600">N/A</span>;
        }

        const basketDetails = basketIds
          .map((id: number) => baskets.find((basket) => basket.id === id))
          .filter(Boolean);

        if (basketDetails.length === 0) {
          return (
            <Tag className="cursor-pointer transition-colors hover:bg-blue-100" color="blue">
              {count}
            </Tag>
          );
        }

        return (
          <Popover
            content={
              <div className="max-h-[150px] max-w-xs space-y-1 overflow-auto">
                {basketDetails.map((basket: any, index: number) => (
                  <div key={index} className="text-sm text-gray-700">
                    • {basket.name}
                  </div>
                ))}
              </div>
            }
            title={t('baskets')}
            trigger="click"
            placement="top"
          >
            <Tag className="cursor-pointer transition-colors hover:bg-blue-100" color="blue">
              {count}
            </Tag>
          </Popover>
        );
      },
    },
    {
      key: 'price',
      title: t('price'),
      dataIndex: 'price',
      width: 100,
      align: 'right' as const,
      render: (value: any, record: any) => (
        <span className="font-medium">
          {record?.status === PriceStatusEnum.PRODUCTNOTFOUND ? (
            <span className="font-semibold text-red-500">{t('price:priceNotFound')}</span>
          ) : value !== undefined && value !== null && !isNaN(Number(value)) ? (
            Number(value).toFixed(2)
          ) : (
            'N/A'
          )}
        </span>
      ),
    },
    {
      key: 'reportedTime',
      title: t('reportedTime'),
      dataIndex: 'reportedTime',
      align: 'center' as const,
      width: 200,
      render: (value: any) => (
        <span className="text-sm text-gray-600">
          {value ? dayjs(new Date(Number(value))).format('YYYY-MM-DD hh:mm:ss A') : 'N/A'}
        </span>
      ),
    },
    {
      key: 'location',
      title: t('location'),
      dataIndex: 'location',
      width: 200,
      render: (value: any, record: any) => {
        const location = record.inspection?.location;
        if (!location) {
          return <span className="text-gray-600">N/A</span>;
        }
        return (
          <span className="text-sm text-gray-700">
            {location.district && location.city
              ? `${location.district}, ${location.city}`
              : location.name || 'N/A'}
          </span>
        );
      },
    },
  ];

  // Define table actions
  const actions = [
    {
      key: 'view',
      label: t('view'),
      type: 'primary' as const,
      onClick: (record: any) => handleViewPrice(record.id),
    },
  ];

  return (
    <>
      {triggeredFrom === 'inspectorPortal' && (
        <>
          <section className="mb-8 mt-12 w-full">
            <span className="flex w-full flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-0">
              <p className="mt-3 text-base font-medium text-headings-font-color">
                {t('pageDescription')}
              </p>
              <Link to="/inspector/price-record">
                <PrimarySquareBtn handleOnClick={() => {}}>
                  <span className="flex flex-row items-center gap-2">
                    <Plus className="text-lg font-bold md:text-xl" /> {t('startNewInspection')}
                  </span>
                </PrimarySquareBtn>
              </Link>
            </span>
          </section>

          <section className="mb-8 w-full">
            <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">{t('filters')}</h3>
                <Button
                  type="link"
                  onClick={clearAllFilters}
                  className="hover:text-primary-dark md:text-md text-sm text-primary"
                  disabled={!enableClearFiltersBtn}
                >
                  {t('clearFilters')}
                </Button>
              </div>

              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">
                      {t('productCategories')}
                    </label>
                  </div>
                  <Select
                    className="w-full [&>.ant-select-selector]:!rounded-lg"
                    placeholder={t('selectCategories')}
                    mode="multiple"
                    allowClear
                    value={selectedProductCategories}
                    onClear={() => setSelectedProductCategories([])}
                    onChange={setSelectedProductCategories}
                    loading={isLoadingFilterData}
                    options={productCategories.map((category: any) => ({
                      label: `${category.mainCategory}${category.category ? ` > ${category.category}` : ''}${category.subCategory ? ` > ${category.subCategory}` : ''}`,
                      value: category.id.toString(),
                    }))}
                    showSearch
                    filterOption={(input, option) =>
                      normalize(option?.label ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(normalize(input.toLowerCase()))
                    }
                  />
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">{t('baskets')}</label>
                  </div>
                  <Select
                    className="w-full [&>.ant-select-selector]:!rounded-lg"
                    placeholder={t('selectBaskets')}
                    mode="multiple"
                    allowClear
                    value={selectedBaskets}
                    onClear={() => {
                      setSelectedBaskets([]);
                      setSelectedProducts([]);
                      setFilteredPresentations(presentations);
                    }}
                    onChange={handleBasketChange}
                    loading={isLoadingFilterData}
                    options={baskets.map((basket: any) => ({
                      label: basket.name,
                      value: basket.id,
                    }))}
                    showSearch
                    filterOption={(input, option) =>
                      normalize(option?.label ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(normalize(input.toLowerCase()))
                    }
                  />
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">{t('products')}</label>
                  </div>
                  <Select
                    className="w-full [&>.ant-select-selector]:!rounded-lg"
                    placeholder={t('selectProducts')}
                    mode="multiple"
                    allowClear
                    value={selectedProducts}
                    onClear={() => setSelectedProducts([])}
                    onChange={setSelectedProducts}
                    loading={isLoadingFilterData}
                    options={filteredPresentations.map((presentation: any) => ({
                      label: presentation.name,
                      value: presentation.refId,
                    }))}
                    showSearch
                    filterOption={(input, option) =>
                      normalize(option?.label ?? '')
                        .toString()
                        .toLowerCase()
                        .includes(normalize(input.toLowerCase()))
                    }
                  />
                </Col>

                <Col xs={24} sm={12} md={8} lg={4}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">{t('dateRange')}</label>
                  </div>
                  <DatePicker.RangePicker
                    className="w-full [&>.ant-picker]:!rounded-lg"
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                    format="YYYY-MM-DD"
                    placeholder={[t('from'), t('to')]}
                    disabledDate={disableFutureDates}
                  />
                </Col>

                <Col xs={24} sm={12} md={8} lg={8}>
                  <div className="mb-2">
                    <label className="text-sm font-medium text-gray-700">{t('priceFilter')}</label>
                  </div>
                  <div className="flex gap-2">
                    <Select
                      className="w-40 [&>.ant-select-selector]:!rounded-lg [&_.ant-select-selection-placeholder]:!text-sm"
                      value={priceFilterCondition}
                      placeholder={t('selectFilter')}
                      allowClear
                      onClear={() => setPriceFilterCondition(undefined)}
                      onChange={setPriceFilterCondition}
                      options={[
                        { label: t('equal'), value: 'equal' },
                        { label: t('lessThan'), value: 'less' },
                        { label: t('greaterThan'), value: 'greater' },
                        { label: t('between'), value: 'between' },
                      ]}
                    />
                    <InputNumber
                      className="w-32 [&>.ant-input-number]:!rounded-lg"
                      placeholder={t('price')}
                      value={priceFilterValue}
                      onChange={setPriceFilterValue}
                      min={0}
                      precision={2}
                    />
                    {priceFilterCondition === 'between' && (
                      <InputNumber
                        className="w-32 [&>.ant-input-number]:!rounded-lg"
                        placeholder={t('to')}
                        value={priceFilterValue2}
                        onChange={setPriceFilterValue2}
                        min={0}
                        precision={2}
                      />
                    )}
                  </div>
                </Col>

                <Col xs={24} sm={12} md={4} lg={4}>
                  <Checkbox
                    checked={isNotInSuggestedPresentation}
                    onChange={(e) => setIsNotInSuggestedPresentation(e.target.checked)}
                  >
                    {t('notInSuggestedPresentation')}
                  </Checkbox>
                </Col>

                <Col xs={24} sm={12} md={4} lg={4}>
                  <Checkbox
                    checked={isNotInSuggestedBrand}
                    onChange={(e) => setIsNotInSuggestedBrand(e.target.checked)}
                  >
                    {t('notInSuggestedBrand')}
                  </Checkbox>
                </Col>
              </Row>
              <Row className="mt-4 text-sm" justify="end" gutter={8}>
                <PrimaryButton
                  handleOnClick={downloadCSVFn}
                  disabled={isdownloadingPrices || !isOnline}
                  className="px-3"
                >
                  {t('downloadCSV')}
                </PrimaryButton>
              </Row>
            </div>
          </section>
        </>
      )}

      <section className="submitted-price-records-table mb-6 w-full">
        <DataTable
          data={priceData.flat(2)}
          columns={columns}
          actions={triggeredFrom === 'inspectorPortal' ? actions : []}
          loading={isLoadingPrices}
          totalItems={totalPrices}
          currentPage={currentPage}
          pageSize={pageSize}
          onPageChange={handlePageChange}
          rowKey="id"
          showSizeChanger={false}
          showQuickJumper={true}
          showTotal={(total, range) => `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`}
          className="price-table"
          size="small"
          scroll={{ x: 1000 }}
        />
      </section>

      <Modal
        title={t('Price Details')}
        open={isViewModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={800}
        centered
      >
        {isLoadingPriceDetails ? (
          <div className="flex items-center justify-center py-8">
            <Spin size="large" />
          </div>
        ) : selectedPrice ? (
          <div className="submitted-price-records-modal py-4">
            <Descriptions
              bordered
              column={1}
              size="middle"
              labelStyle={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}
            >
              <Descriptions.Item label={t('presentation')}>
                {selectedPrice?.product?.name || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label={t('Price')}>
                {selectedPrice?.status === PriceStatusEnum.PRODUCTNOTFOUND ? (
                  <Tag color="red" className="text-lg font-semibold">
                    {t('price:priceNotFound')}
                  </Tag>
                ) : (
                  <Tag color="green" className="text-lg font-semibold">
                    L. {selectedPrice?.price || 'N/A'}
                  </Tag>
                )}
              </Descriptions.Item>

              <Descriptions.Item label={t('Establishment')}>
                {selectedPrice?.establishment_name || 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label={t('Location')}>
                {selectedPrice?.inspection?.location
                  ? `${selectedPrice.inspection.location.district}, ${selectedPrice.inspection.location.city}`
                  : 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label={t('Reported Date')}>
                {selectedPrice?.reportedTime
                  ? dayjs(Number(selectedPrice.reportedTime)).format('YYYY-MM-DD HH:mm')
                  : 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label={t('Created Date')}>
                {selectedPrice?.createTime
                  ? dayjs(Number(selectedPrice.createTime)).format('YYYY-MM-DD HH:mm')
                  : 'N/A'}
              </Descriptions.Item>

              <Descriptions.Item label={t('Last Updated')}>
                {selectedPrice?.updateTime
                  ? dayjs(Number(selectedPrice.updateTime)).format('YYYY-MM-DD HH:mm')
                  : 'N/A'}
              </Descriptions.Item>

              {/* <Descriptions.Item label={t("Status")}>
                <Tag color={selectedPrice?.isActive ? 'green' : 'red'}>
                  {selectedPrice?.isActive ? 'Active' : 'Inactive'}
                </Tag>
              </Descriptions.Item> */}

              {selectedPrice?.productRemarks && (
                <Descriptions.Item label={t('Remarks')}>
                  {selectedPrice.productRemarks}
                </Descriptions.Item>
              )}

              {selectedPrice?.refId && (
                <Descriptions.Item label={t('Reference ID')}>
                  <code className="rounded bg-gray-100 px-2 py-1">{selectedPrice.refId}</code>
                </Descriptions.Item>
              )}

              {selectedPrice?.photoOrInvoice && (
                <Descriptions.Item label={t('photoOrInvoice')}>
                  <div className="grid max-h-[200px] gap-4 overflow-auto">
                    {selectedPrice?.photoOrInvoice.split(',').map((image: any) => (
                      <Image
                        src={image}
                        alt={t('photoOrInvoice')}
                        className="m-0 h-full max-h-[200px] w-full object-cover"
                        loading="lazy"
                        placeholder={
                          <div className="flex h-[200px] items-center justify-center bg-gray-100">
                            <Spin />
                          </div>
                        }
                      />
                    ))}
                  </div>
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>
        ) : (
          <div className="py-8 text-center text-gray-500">{t('noPriceDetailsAvailable')}</div>
        )}
      </Modal>
    </>
  );
};

export default MyPrices;
